import type {
  AdminAdjustmentRequest,
  AdminAdjustmentResponse,
  AdminCreateLedgerRequest,
  AdminCreateLedgerResponse,
  AdminResetLedgerRequest,
  AdminResetLedgerResponse,
  AdminSetFreeRequest,
  AdminSetHardFloorRequest,
  AdminSuspendRequest,
  AdminUnsuspendRequest,
  AdminSetTrustedRequest,
  UpdateLedgerEntryRequest,
} from '../types';
import type { BillingProfileResponse } from '../../types';
import type { RequestFn } from '../../client';

/**
 * Platform-level ledger adjustments on a merchant's balance. Admin-only
 * routes under `/billing/{merchantId}/admin/credit|debit`. Exposed only
 * via `'@delopay/sdk/internal'` so the public merchant SDK doesn't
 * advertise the admin adjustment path in autocomplete.
 */
export class PlatformBilling {
  constructor(private readonly request: RequestFn) {}

  /**
   * Manually credit a merchant's balance (e.g. promotional credit,
   * dispute reversal, manual correction).
   *
   * @param merchantId - The merchant account ID.
   * @param params - Credit amount and reason.
   */
  async credit(
    merchantId: string,
    params: AdminAdjustmentRequest,
  ): Promise<AdminAdjustmentResponse> {
    return this.request('POST', `/billing/${encodeURIComponent(merchantId)}/admin/credit`, {
      body: params,
    });
  }

  /**
   * Manually debit a merchant's balance.
   *
   * @param merchantId - The merchant account ID.
   * @param params - Debit amount and reason.
   */
  async debit(
    merchantId: string,
    params: AdminAdjustmentRequest,
  ): Promise<AdminAdjustmentResponse> {
    return this.request('POST', `/billing/${encodeURIComponent(merchantId)}/admin/debit`, {
      body: params,
    });
  }

  /**
   * Edit a single ledger entry (amount and/or description), keeping the
   * merchant balance consistent: when `amount` changes, the balance is
   * atomically adjusted by the difference. Returns the entry id and new
   * balance.
   *
   * @param merchantId - The merchant account ID.
   * @param ledgerId - The ledger entry id to edit.
   * @param params - New amount / description (either optional).
   */
  async editLedgerEntry(
    merchantId: string,
    ledgerId: string,
    params: UpdateLedgerEntryRequest,
  ): Promise<AdminAdjustmentResponse> {
    return this.request(
      'PATCH',
      `/billing/${encodeURIComponent(merchantId)}/admin/ledger/${encodeURIComponent(ledgerId)}`,
      { body: params },
    );
  }

  /**
   * Delete a single ledger entry, reversing its amount from the merchant
   * balance. Returns the deleted entry id and the new balance.
   *
   * @param merchantId - The merchant account ID.
   * @param ledgerId - The ledger entry id to delete.
   */
  async deleteLedgerEntry(merchantId: string, ledgerId: string): Promise<AdminAdjustmentResponse> {
    return this.request(
      'DELETE',
      `/billing/${encodeURIComponent(merchantId)}/admin/ledger/${encodeURIComponent(ledgerId)}`,
    );
  }

  /**
   * Manually suspend a merchant (e.g. confirmed fraud or ToS violation),
   * independent of balance. A `reason` is required for audit. Throws 412 if
   * the merchant is trusted (clear the flag first) or already suspended.
   *
   * @param merchantId - The merchant account ID.
   * @param params - Suspension reason.
   * @returns The updated billing profile.
   */
  async suspend(merchantId: string, params: AdminSuspendRequest): Promise<BillingProfileResponse> {
    return this.request('POST', `/billing/${encodeURIComponent(merchantId)}/admin/suspend`, {
      body: params,
    });
  }

  /**
   * Lift a suspension. Restores the merchant to `active` (or `delinquent` if
   * the balance is at/below the hard floor) and resets the recharge-failure
   * counter. Throws 412 if the merchant is not suspended.
   *
   * @param merchantId - The merchant account ID.
   * @param params - Optional audit note.
   * @returns The updated billing profile.
   */
  async unsuspend(
    merchantId: string,
    params: AdminUnsuspendRequest = {},
  ): Promise<BillingProfileResponse> {
    return this.request('POST', `/billing/${encodeURIComponent(merchantId)}/admin/unsuspend`, {
      body: params,
    });
  }

  /**
   * Set or clear the trusted (suspension-exempt) flag. Trusted merchants
   * cannot be suspended automatically or manually. Does not lift an existing
   * suspension — use {@link PlatformBilling.unsuspend} for that.
   *
   * @param merchantId - The merchant account ID.
   * @param params - The desired trusted state.
   * @returns The updated billing profile.
   */
  async setTrusted(
    merchantId: string,
    params: AdminSetTrustedRequest,
  ): Promise<BillingProfileResponse> {
    return this.request('PATCH', `/billing/${encodeURIComponent(merchantId)}/admin/trusted`, {
      body: params,
    });
  }

  /**
   * Set the merchant's hard floor — the balance at or below which the
   * merchant stops being active. A negative value grants a credit line; zero
   * means no negative balance at all. This moves the suspension line, it does
   * not remove it. The value and the caller are written to the audit log
   * before the floor changes, so the change is always attributable.
   *
   * Refused (412) for a positive amount, for a floor beyond the maximum
   * credit line of 10,000,000 minor units, or for an empty `reason`.
   *
   * Requires a router that serves the hard-floor admin route; older ones
   * answer `404`.
   *
   * @param merchantId - The merchant account ID.
   * @param params - The new floor (zero or negative) and the audit reason.
   * @returns The updated billing profile.
   */
  async setHardFloor(
    merchantId: string,
    params: AdminSetHardFloorRequest,
  ): Promise<BillingProfileResponse> {
    return this.request('PATCH', `/billing/${encodeURIComponent(merchantId)}/admin/hard-floor`, {
      body: params,
    });
  }

  /**
   * Mark a merchant free (not billed), or put it back on the prepaid model.
   * While free, no platform fee is deducted, the payment gate never blocks on
   * billing status, top-ups are refused and auto-recharge is inert. Marking
   * free needs a `reason` and is refused (412) while an admin suspension is in
   * force — lift that first.
   *
   * Requires a router that supports the free flag; older ones answer `404`.
   *
   * @param merchantId - The merchant account ID.
   * @param params - The desired free state and, when marking free, the reason.
   * @returns The updated billing profile.
   */
  async setFree(merchantId: string, params: AdminSetFreeRequest): Promise<BillingProfileResponse> {
    return this.request('PATCH', `/billing/${encodeURIComponent(merchantId)}/admin/free`, {
      body: params,
    });
  }

  /**
   * Delete a merchant's **entire** ledger. Every live entry is soft-deleted —
   * kept, so a replayed fee deduction or top-up webhook still no-ops — the
   * balance is set to zero and the status re-derived (`active` for a free
   * merchant; an active paying merchant lands in `delinquent`). Refused (412)
   * while the merchant has shop allocations. Audited.
   *
   * Requires a router that serves the ledger admin routes; older ones answer
   * `404`.
   *
   * @param merchantId - The merchant account ID.
   * @param params - The reason, required for audit.
   * @returns What was removed and the post-reset profile.
   */
  async resetLedger(
    merchantId: string,
    params: AdminResetLedgerRequest,
  ): Promise<AdminResetLedgerResponse> {
    return this.request('DELETE', `/billing/${encodeURIComponent(merchantId)}/admin/ledger`, {
      body: params,
    });
  }

  /**
   * (Re)create a merchant's ledger, optionally seeded with an opening
   * balance. Ensures the billing profile exists (created without the welcome
   * promo credit) and, when `opening_balance` is set, writes one
   * `opening_balance` entry through the same atomic helper an admin credit
   * uses. Refused (412) while live entries or a non-zero balance remain —
   * reset first. Does not clear the free flag. Audited.
   *
   * Requires a router that serves the ledger admin routes; older ones answer
   * `404`.
   *
   * @param merchantId - The merchant account ID.
   * @param params - Opening balance (minor units), currency for a new profile, and the reason.
   * @returns The opening entry id (if one was written) and the profile.
   */
  async createLedger(
    merchantId: string,
    params: AdminCreateLedgerRequest,
  ): Promise<AdminCreateLedgerResponse> {
    return this.request('POST', `/billing/${encodeURIComponent(merchantId)}/admin/ledger`, {
      body: params,
    });
  }
}
